import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { useAuth } from '../../context/AuthContext';
import { useAsync } from '../../lib/useAsync';
import { storiesApi } from '../../api/resources';
import { Stat, StatusBadge, SkeletonRows } from '../../components/ui';
import { formatDate } from '../../lib/format';

export default function MemberOverview() {
  const { user } = useAuth();
  // Members see their own non-published + published stories from this endpoint.
  const { data, loading } = useAsync(() => storiesApi.list({ per_page: 50 }), []);
  const items = data?.items || [];
  const mine = items.filter((s) => s.author?.id === user?.id);

  const counts = mine.reduce((acc, s) => {
    acc[s.status] = (acc[s.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <Seo title="Member Dashboard" />
      <div>
        <h1 className="font-display text-2xl font-bold">Welcome, {user?.name?.split(' ')[0]}</h1>
        <p className="text-ink-muted">Here's an overview of your contributions.</p>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Stat value={mine.length} label="Total Stories" icon="📝" />
        <Stat value={counts.published || 0} label="Published" icon="✅" />
        <Stat value={counts.pending || 0} label="Pending Review" icon="⏳" />
        <Stat value={counts.draft || 0} label="Drafts" icon="📄" />
      </div>

      <div className="card p-6">
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-ink">Your recent stories</h2>
          <Link to="/member/stories/new" className="btn-primary px-4 py-2 text-sm">
            + New Story
          </Link>
        </div>
        {loading ? (
          // The loaded list is `ul.divide-y` rows (title + date, status pill on
          // the right) capped at 5, inside this card — not a card grid. Count
          // matches that cap, so only a shorter result set shifts the page.
          <SkeletonRows count={5} variant="divided" />
        ) : mine.length === 0 ? (
          <p className="py-8 text-center text-sm text-ink-muted">
            You haven't written any stories yet.{' '}
            <Link to="/member/stories/new" className="font-semibold text-maroon-700">
              Write your first one.
            </Link>
          </p>
        ) : (
          <ul className="divide-y divide-cream-300">
            {mine.slice(0, 5).map((s) => (
              <li key={s.id} className="flex items-center justify-between py-3">
                <div className="min-w-0">
                  <Link to={`/member/stories/${s.id}/edit`} className="font-medium text-ink hover:text-maroon-700">
                    {s.title}
                  </Link>
                  <p className="text-xs text-ink-muted">{formatDate(s.updated_at || s.created_at)}</p>
                </div>
                <StatusBadge status={s.status} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
